import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { 
  Upload, 
  FileText, 
  Image, 
  Shield, 
  GraduationCap, 
  Calendar,
  Trash2,
  Download,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  File,
  Camera,
  Loader2
} from 'lucide-react';

// Document categories
const DOCUMENT_CATEGORIES = [
  { value: 'profile_photo', label: 'Profile Photo', icon: Camera, requiresExpiry: false },
  { value: 'dbs', label: 'DBS Certificate', icon: Shield, requiresExpiry: true },
  { value: 'qualification', label: 'Qualification/Degree', icon: GraduationCap, requiresExpiry: false },
  { value: 'certification', label: 'Professional Certification', icon: FileText, requiresExpiry: true },
  { value: 'training', label: 'Training Certificate', icon: GraduationCap, requiresExpiry: true },
  { value: 'id_document', label: 'ID Document (Passport/Driving License)', icon: FileText, requiresExpiry: true },
  { value: 'right_to_work', label: 'Right to Work', icon: Shield, requiresExpiry: true },
  { value: 'reference', label: 'Reference Letter', icon: FileText, requiresExpiry: false },
  { value: 'contract', label: 'Employment Contract', icon: FileText, requiresExpiry: false },
  { value: 'other', label: 'Other Document', icon: File, requiresExpiry: false }
];

const getCategoryIcon = (category) => {
  const cat = DOCUMENT_CATEGORIES.find(c => c.value === category);
  return cat?.icon || FileText;
};

const getCategoryLabel = (category) => {
  const cat = DOCUMENT_CATEGORIES.find(c => c.value === category);
  return cat?.label || category;
};

const categoryRequiresExpiry = (category) => {
  const cat = DOCUMENT_CATEGORIES.find(c => c.value === category);
  return cat?.requiresExpiry || false;
};

// Personal Documents Manager Component
export function PersonalDocumentsManager({ userId, userName, onProfilePhotoUpdated }) {
  const { api, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewingDocId, setViewingDocId] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDeleteRequestDialog, setShowDeleteRequestDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [uploadForm, setUploadForm] = useState({
    category: '',
    description: '',
    file: null,
    expiry_date: '',
    dbs_number: ''
  });

  // Check if user can delete directly
  const canDeleteDirectly = ['director', 'managing_director', 'admin', 'hr'].includes(user?.role) || 
                            user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await api.get(`/uploads/entity/staff/${userId}`);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  useEffect(() => {
    if (userId) {
      fetchDocuments();
    }
  }, [userId, fetchDocuments]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB for most docs)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.category) {
      toast.error('Please select a file and category');
      return;
    }

    // Validate expiry date for categories that require it
    if (categoryRequiresExpiry(uploadForm.category) && !uploadForm.expiry_date) {
      toast.error(`Please enter the expiry date for ${getCategoryLabel(uploadForm.category)}`);
      return;
    }

    // Validate DBS number if DBS certificate
    if (uploadForm.category === 'dbs' && !uploadForm.dbs_number) {
      toast.error('Please enter your DBS certificate number');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('entity_type', 'staff');
      formData.append('entity_id', userId);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description || `${getCategoryLabel(uploadForm.category)}`);
      
      // Add expiry date if provided
      if (uploadForm.expiry_date) {
        formData.append('expiry_date', uploadForm.expiry_date);
      }
      
      // Add DBS number if DBS certificate
      if (uploadForm.category === 'dbs' && uploadForm.dbs_number) {
        formData.append('dbs_number', uploadForm.dbs_number);
      }

      const response = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploaded successfully! It has been securely saved.');
      setShowUploadDialog(false);
      setUploadForm({ category: '', description: '', file: null, expiry_date: '', dbs_number: '' });
      fetchDocuments();
      
      // If profile photo was uploaded, notify parent to refresh profile
      if (uploadForm.category === 'profile_photo' && onProfilePhotoUpdated) {
        onProfilePhotoUpdated();
      }
      
      // Show additional confirmation for DBS
      if (uploadForm.category === 'dbs') {
        toast.info('Your DBS certificate has been saved. You will be notified 2 months before expiry.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!selectedDocument || !deleteReason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }

    try {
      await api.post('/uploads/request-action', {
        attachment_id: selectedDocument.id,
        action: 'delete',
        reason: deleteReason.trim()
      });

      toast.success('Delete request submitted for authorization');
      setShowDeleteRequestDialog(false);
      setSelectedDocument(null);
      setDeleteReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleDirectDelete = async (doc) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.original_filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/uploads/${doc.id}`);
      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleViewDocument = async (doc) => {
    setViewingDocId(doc.id);
    try {
      // Fetch the file with authentication
      const response = await api.get(`/uploads/files/${doc.id}`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and open it
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      
      // For images and PDFs, open in new tab
      // For other files, trigger download
      const fileType = doc.file_format || doc.file_type;
      if (['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileType?.toLowerCase())) {
        window.open(url, '_blank');
      } else {
        // For other document types, download
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.original_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Clean up the blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error) {
      toast.error('Failed to open document. Please try again.');
      console.error('View document error:', error);
    } finally {
      setViewingDocId(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
          <p className="text-slate-500 mt-2">Loading documents...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1e3a5f]" />
              My Documents
            </CardTitle>
            <CardDescription>
              Upload and manage your professional documents, certificates, and ID
            </CardDescription>
          </div>
          <Button 
            onClick={() => setShowUploadDialog(true)}
            className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
            data-testid="upload-document-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Info Alert */}
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <Shield className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Document Policy:</strong> You can upload any documents to your profile. 
            To delete or amend a document, you'll need authorization from HR, Admin, or a Director.
          </AlertDescription>
        </Alert>

        {/* Documents Grid */}
        {documents.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No documents uploaded yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Upload your DBS certificate, qualifications, and other important documents
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setShowUploadDialog(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Your First Document
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((doc) => {
              const CategoryIcon = getCategoryIcon(doc.category);
              return (
                <div 
                  key={doc.id}
                  className="p-4 border rounded-lg hover:border-[#1e3a5f]/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <CategoryIcon className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate" title={doc.original_filename}>
                        {doc.original_filename}
                      </p>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {getCategoryLabel(doc.category)}
                      </Badge>
                      <p className="text-xs text-slate-400 mt-1">
                        Uploaded: {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleViewDocument(doc)}
                      disabled={viewingDocId === doc.id}
                    >
                      {viewingDocId === doc.id ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Opening...
                        </>
                      ) : (
                        <>
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </>
                      )}
                    </Button>
                    {canDeleteDirectly ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDirectDelete(doc)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-amber-600 hover:bg-amber-50"
                        onClick={() => {
                          setSelectedDocument(doc);
                          setShowDeleteRequestDialog(true);
                        }}
                        title="Request deletion"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#1e3a5f]" />
              Upload Document
            </DialogTitle>
            <DialogDescription>
              Upload your professional documents and certificates
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="category">Document Type *</Label>
              <Select 
                value={uploadForm.category}
                onValueChange={(value) => setUploadForm(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger data-testid="document-category-select">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="file">File *</Label>
              <Input
                id="file"
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                onChange={handleFileChange}
                data-testid="document-file-input"
              />
              <p className="text-xs text-slate-500">
                Accepted formats: PDF, DOC, DOCX, JPG, PNG (max 10MB)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                placeholder="Add a description or notes about this document"
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            {/* DBS Number - only for DBS certificates */}
            {uploadForm.category === 'dbs' && (
              <div className="space-y-2">
                <Label htmlFor="dbs_number">DBS Certificate Number *</Label>
                <Input
                  id="dbs_number"
                  placeholder="Enter your DBS certificate number"
                  value={uploadForm.dbs_number}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, dbs_number: e.target.value }))}
                />
              </div>
            )}

            {/* Expiry Date - for documents that expire */}
            {categoryRequiresExpiry(uploadForm.category) && (
              <div className="space-y-2">
                <Label htmlFor="expiry_date">
                  Expiry Date *
                  {uploadForm.category === 'dbs' && (
                    <span className="text-xs text-slate-500 ml-2">(You'll be notified 2 months before)</span>
                  )}
                </Label>
                <Input
                  id="expiry_date"
                  type="date"
                  value={uploadForm.expiry_date}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-green-600">
                  <CheckCircle className="w-3 h-3 inline mr-1" />
                  You and HR will be notified before this document expires
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload}
              disabled={uploading || !uploadForm.file || !uploadForm.category}
              className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
              data-testid="submit-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Request Dialog */}
      <Dialog open={showDeleteRequestDialog} onOpenChange={setShowDeleteRequestDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Request Document Deletion
            </DialogTitle>
            <DialogDescription>
              Document deletion requires authorization from HR, Admin, or a Director
            </DialogDescription>
          </DialogHeader>

          {selectedDocument && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium text-slate-900">{selectedDocument.original_filename}</p>
                <p className="text-sm text-slate-500">{getCategoryLabel(selectedDocument.category)}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delete-reason">Reason for Deletion *</Label>
                <Textarea
                  id="delete-reason"
                  placeholder="Please explain why this document needs to be deleted..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={3}
                />
              </div>

              <Alert className="bg-amber-50 border-amber-200">
                <Clock className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  Your request will be reviewed by authorized personnel. 
                  You will be notified once a decision is made.
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteRequestDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteRequest}
              disabled={!deleteReason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default PersonalDocumentsManager;
